/**
 * Wrapper for fetch which includes access token refreshes.
 */

import {apiHost, updateStandaloneAccessToken} from '../report/util';
import NamedError from '../report/queries/utils/named-error';

const DEFAULT_MAX_AUTH_RETRIES = 5;

class ResponseError extends NamedError {
  constructor(response) {
    super(response.statusText);
    this.response = response;
  }
}

class AppApiClient {
  constructor({maxAuthRetries}={}) {
    this.refreshTokenUrl = `/oauth/access_token/`;
    this.authRetries = 0;
    this.maxAuthRetries = maxAuthRetries || DEFAULT_MAX_AUTH_RETRIES;
  }

  get token() {
    return this.accessToken;
  }

  set token(newValue) {
    this.accessToken = newValue;
    updateStandaloneAccessToken(this.accessToken);
  }

  _getNewAccessToken() {
    if (this._pendingRefresh) {
      return this._pendingRefresh;
    }

    this._pendingRefresh =
      fetch(`${apiHost()}${this.refreshTokenUrl}`, {credentials: `include`})
        .then(response => {
          if (!response.ok) {
            return Promise.reject(response.statusText);
          } else {
            return response.json();
          }
        })
        .catch(() => delete this._pendingRefresh)
        .then(response => {
          // In the case that the user is legitimately logged out, the oauth token
          // request 302s to the normal html login page, which 200s, and then
          // response.json() returns true. I wish it would fail instead, but for
          // now there's this check.
          if (!response.token) {
            // clear cached token in standalone mode so next browser refresh fetches a new one
            updateStandaloneAccessToken(null);
            delete this._pendingRefresh;
            return Promise.reject(`No token in response`);
          }

          this.token = response.token;
          delete this._pendingRefresh;
        });

    return this._pendingRefresh;
  }

  fetch(url, options) {
    if (!this.token) {
      return this._getNewAccessToken().then(() => this._fetch(url, options));
    } else {
      return this._fetch(url, options);
    }
  }

  _fetch(url, options) {
    const {
      headers={},
      method=`GET`,
      body=undefined,
      params={},
      throwOnInvalidStatus=false,
    } = options || {};
    const _params = Object.assign(
      {},
      {
        headers: {
          'Authorization': `Bearer ${this.token}`,
          ...headers,
        },
        method,
        body,
      },
      params);

    return fetch(url, _params)
      .then(response => {
        if (throwOnInvalidStatus && (response.status >= 400)) {
          throw new ResponseError(response);
        }

        if (response.status >= 500) {
          this.authRetries = 0;
          throw new Error(response.statusText || response.status);
        } else if (response.status === 401) {
          this.token = null;
          if (this.authRetries++ < this.maxAuthRetries) {
            return this.fetch(url, options);
          } else {
            throw new Error(`Authorization failed, ${this.authRetries} retries`);
          }
        } else {
          this.authRetries = 0;
          return response;
        }
      });
  }

  post(url, data, optionsOverride={}) {
    return this.fetch(url, {method: `POST`, body: data, ...optionsOverride}).then(response => response.json());
  }

  patch(url, data, optionsOverride={}) {
    return this.fetch(url, {method: `PATCH`, body: data, ...optionsOverride}).then(response => response.json());
  }

  delete(url, optionsOverride={}) {
    return this.fetch(url, {method: `DELETE`, ...optionsOverride}).then(response => response.json());
  }

  processResponse(response) {
    if (response.ok || response.status === 200) {
      return response.json().then(results => results.results);
    } else {
      return response.json().then(results => {
        console.error(response.status, results.error);
        return {
          error: results.error,
          status: response.status,
        };
      });
    }
  }
}

let defaultInstance;
if (typeof window !== `undefined`) {
  if (!window.__appApiClient) {
    window.__appApiClient = new AppApiClient();
  } else { /* this module has already been loaded in another bundle */}

  defaultInstance = window.__appApiClient;
} else {
  defaultInstance = new AppApiClient();
}

export default defaultInstance;
